/**
 * Times on this app are shown in the salon's zone, never the browser's. A receptionist on a laptop
 * set to UTC must still see the 11:00 booking at 11:00 (TDD-SA: instants are stored in UTC and read
 * in the branch's zone). Every screen passes the branch's `timezone`.
 *
 * How a time is written is the salon's choice, `salon.public.time_format` (TDD-SA FR-SA-140):
 * "4:30 pm" or "16:30". Screens get both the zone and the choice from `useSalonClock`.
 */

export type ClockFormat = "12-hour" | "24-hour";

export interface ClockOptions {
  /** Default 24-hour, the form these helpers have always written; the app passes the salon's choice. */
  clock?: ClockFormat;
  locale?: string;
  /**
   * On the 12-hour clock a whole hour is "1 pm" by default; `full` keeps "1:00 pm", for a column of
   * times that should line up (the time wheel and the week grid).
   */
  wholeHour?: "short" | "full";
}

/** The narrow and the ordinary no-break space, built from their code points so the source stays plain ASCII. */
const ODD_SPACES = new RegExp(`[${String.fromCharCode(0x202f, 0xa0)}]`, "g");

/** ICU writes a narrow no-break space before "pm" in newer releases; one plain space reads the same everywhere. */
function plainSpaces(text: string): string {
  return text.replace(ODD_SPACES, " ");
}

/**
 * "2026-10-05T05:30:00Z" in Asia/Kolkata -> "11:00", or "11 am" on the 12-hour clock. On the 12-hour
 * clock a whole hour drops its ":00", as people write it and as the opening hours already read.
 */
export function formatTime(
  instant: string,
  timeZone: string,
  { clock = "24-hour", locale = "en-IN", wholeHour = "short" }: ClockOptions = {},
): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  if (clock === "12-hour") {
    const text = plainSpaces(
      new Intl.DateTimeFormat(locale, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone,
      }).format(date),
    ).toLowerCase();
    return wholeHour === "full" ? text : text.replace(/:00(?= )/, "");
  }
  return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone }).format(date);
}

/** "Mon, 5 Oct" in the salon's zone. */
export function formatDay(instant: string, timeZone: string, locale = "en-IN"): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  return plainSpaces(new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short", timeZone }).format(date));
}

/** "Mon, 5 Oct, 11:00" (or "Mon, 5 Oct, 11 am") in the salon's zone, for lists that span days. */
export function formatDateTime(instant: string, timeZone: string, options: ClockOptions = {}): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  return `${formatDay(instant, timeZone, options.locale)}, ${formatTime(instant, timeZone, options)}`;
}

/**
 * "4:30 – 5:20 pm" or "11:30 am – 12:20 pm": a start and an end, the meridiem said once when both
 * share it. On the 24-hour clock, "16:30 – 17:20".
 */
export function formatTimeRange(start: string, end: string, timeZone: string, options: ClockOptions = {}): string {
  const from = formatTime(start, timeZone, options);
  const to = formatTime(end, timeZone, options);
  if (options.clock === "12-hour") {
    const suffix = / (am|pm)$/;
    const a = suffix.exec(from)?.[1];
    if (a && a === suffix.exec(to)?.[1]) return `${from.replace(suffix, "")} – ${to}`;
  }
  return `${from} – ${to}`;
}

/** The calendar parts of an instant in the salon's zone, for the date blocks on cards. */
export function dateParts(instant: string, timeZone: string, locale = "en-IN") {
  const date = new Date(instant);
  const part = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date);
  return {
    weekday: part({ weekday: "short" }),
    weekdayLong: part({ weekday: "long" }),
    day: part({ day: "numeric" }),
    month: part({ month: "short" }),
    monthLong: part({ month: "long" }),
  };
}

/** Today's date in the salon's zone as yyyy-mm-dd, the form every salon date parameter takes. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).format(now);
  return parts;
}

/** A yyyy-mm-dd plus whole days, without time-zone drift. */
export function addDays(date: string, days: number): string {
  const [year = Number.NaN, month = Number.NaN, day = Number.NaN] = date.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return Number.isNaN(next.getTime()) ? date : next.toISOString().slice(0, 10);
}

/** Whole days from one yyyy-mm-dd to another; negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  const at = (value: string) => Date.parse(`${value}T00:00:00Z`);
  return Math.round((at(to) - at(from)) / 86_400_000);
}
